import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
// 行高常量定义
const ROW_HEIGHT = 34;
const LEVEL1_GROUP_EXTRA_HEIGHT = 8;
const getGkProp = (level) => `gk${level}`; // 当前分组级别（1/2/3）对应的groupKey属性名
export function useVirtualScroll(dataSource, containerRef, groupState, groupNumber, createGroupMap, bufferCount = 5) {
  // 根据行类型获取预估高度
  const getEstimatedHeight = (item) => {
    if (!item) return 0;
    if (item.level === 1 && groupState.value[item.groupKey]) {
      return ROW_HEIGHT + LEVEL1_GROUP_EXTRA_HEIGHT;
    }
    return ROW_HEIGHT;
  };
  // 计算实际渲染的数据源
  const renderDataSource = computed(() => {
    const source = dataSource.value;
    if (!source || !source.length) return [];
    // 使用更高效的数据处理方式
    const result = [];
    const groupValues = groupState.value;
    const shouldIncludeCache = new WeakMap(); // 缓存计算结果
    for (let i = 0, len = source.length; i < len; i++) {
      const item = source[i];
      if (!item) continue;
      // 使用缓存检查
      if (shouldIncludeCache.has(item)) {
        if (shouldIncludeCache.get(item)) {
          result.push(item);
        }
        continue;
      }
      let shouldInclude = true;
      if (item?.level && item?.groupKey) {
        // 分组标题行处理
        if (item.level > 1) {
          for (let j = 1; j < item.level; j++) {
            const parentGk = item[getGkProp(j)];
            if (!parentGk || !groupValues[parentGk]) {
              shouldInclude = false;
              break;
            }
          }
        }
      } else {
        // 数据行处理
        for (let j = 1; j <= groupNumber; j++) {
          const groupKey = item[getGkProp(j)];
          if (groupKey === undefined || !groupValues[groupKey]) {
            shouldInclude = false;
            break;
          }
        }
      }
      shouldIncludeCache.set(item, shouldInclude);
      if (shouldInclude) {
        result.push(item);
      }
    }
    return result;
  });
  console.log(600000, renderDataSource.value);
  // 位置信息数组
  const positions = ref([]);
  const updatePositions = () => {
    const data = renderDataSource.value;
    if (!data || !data.length) {
      positions.value = [];
      return;
    }
    // 使用更高效的位置计算方式
    const newPositions = new Array(data.length);
    let currentTop = 0;
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const height = getEstimatedHeight(item);
      newPositions[i] = {
        index: i,
        height,
        top: currentTop,
        bottom: currentTop + height,
        id: item._uniqueId || item.id || item.uniqueMore
      };
      currentTop = newPositions[i].bottom;
    }
    positions.value = newPositions;
  };
  // 总高度计算
  const totalHeight = computed(() => {
    if (positions.value.length === 0) return 0;
    return positions.value[positions.value.length - 1].bottom;
  });
  // 更新分组状态
  const updateGroupState = (groupKey, isExpanded) => {
    if (groupState.value[groupKey] === isExpanded) return;
    const oldScrollTop = scrollTop.value;
    groupState.value[groupKey] = isExpanded;
    nextTick(() => {
      updatePositions();
      calculateVisibleRange();
      containerRef.value?.scrollTo({ top: oldScrollTop });
    });
  };

  // 辅助方法
  const visibleData = ref([]);
  const paddingTop = ref(0);
  const paddingBottom = ref(0);
  const scrollTop = ref(0);
  const containerHeight = ref(0);
  const isUpdating = ref(false);
  // 判断是否有滚动容器
  const ensureContainer = () => {
    return containerRef.value && containerRef.value.clientHeight > 0;
  };
  const updateContainerHeight = () => {
    if (ensureContainer()) {
      containerHeight.value = containerRef.value.clientHeight;
    }
  };
  // 二分查找索引
  const findStartIndex = (scrollTopValue) => {
    const positionsArr = positions.value;
    let low = 0;
    let high = positionsArr.length - 1;
    let result = positionsArr.length;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const midPos = positionsArr[mid];
      if (!midPos) {
        // 处理意外情况
        break;
      }
      if (midPos.bottom > scrollTopValue) {
        result = mid;
        high = mid - 1;
      } else {
        low = mid + 1;
      }
    }
    return result;
  };
  const onGroupEnterViewport = ref(null);
  // 判断分组是否未加载（根据当前级别动态判断）
  const isGroupUnloaded = (groupKey, data) => {
    const targetGkProp = getGkProp(groupNumber);
    // 只需要找到一个未加载的数据行即可返回
    for (const item of data) {
      if (!item.level && item[targetGkProp] === groupKey && item.hasData === false) {
        return true;
      }
    }
    return false;
  };
  // 计算可见范围
  const calculateVisibleRange = () => {
    if (isUpdating.value || !ensureContainer() || !positions.value.length) return;
    isUpdating.value = true;
    // 使用性能更高的查找方式
    const positionsArr = positions.value;
    const data = renderDataSource.value;
    const scrollTopValue = scrollTop.value;
    const containerHeightValue = containerHeight.value;
    // 使用插值查找代替二分查找可能更快
    const startIdx = findStartIndex(scrollTopValue);
    const endIdx = findStartIndex(scrollTopValue + containerHeightValue);
    // 动态计算缓冲区大小
    const viewportRowCount = Math.ceil(containerHeightValue / ROW_HEIGHT);
    const dynamicBuffer = Math.min(
      Math.max(bufferCount, Math.floor(viewportRowCount * 0.5)),
      50 // 设置最大缓冲区限制
    );
    const startBuf = Math.max(0, startIdx - dynamicBuffer);
    const endBuf = Math.min(positionsArr.length - 1, endIdx + dynamicBuffer);
    // 延迟处理分组进入视口的逻辑
    if (onGroupEnterViewport.value) {
      setTimeout(() => {
        processGroupEnterViewport(startBuf, endBuf, data);
      }, 0);
    }
    updateVisibleData(startBuf, endBuf);
    isUpdating.value = false;
  };

  // 将分组处理逻辑提取为单独函数
  const processGroupEnterViewport = (startBuf, endBuf, data) => {
    const targetGkProp = getGkProp(groupNumber);
    const checkedGroups = new Set();

    for (let i = startBuf; i <= endBuf; i++) {
      const item = data[i];
      if (!item || item.level) continue;

      const groupKey = item[targetGkProp];
      if (!groupKey || checkedGroups.has(groupKey)) continue;

      checkedGroups.add(groupKey);
      const groupItem = createGroupMap.get(groupKey);

      if (groupItem && groupItem.dataIds?.length && groupState.value[groupKey] && isGroupUnloaded(groupKey, data)) {
        onGroupEnterViewport.value({
          groupKey,
          dataIds: groupItem.dataIds,
          level: groupNumber
        });
      }
    }
  };

  const updateVisibleData = (startBuf, endBuf) => {
    const data = renderDataSource.value;
    const pos = positions.value;
    const newVisibleData = [];
    for (let i = startBuf; i <= endBuf; i++) {
      if (!data[i] || !pos[i]) continue;
      newVisibleData.push({
        ...data[i],
        $pos: {
          index: i,
          top: pos[i].top,
          height: pos[i].height
        }
      });
    }
    visibleData.value = newVisibleData;
    paddingTop.value = startBuf > 0 ? pos[startBuf - 1].bottom : 0;
    paddingBottom.value = totalHeight.value - (endBuf < pos.length - 1 ? pos[endBuf].bottom : totalHeight.value);
  };

  // 滚动处理
  let scrollAnimationFrame = null;
  const handleScroll = () => {
    if (!ensureContainer() || isUpdating.value) return;
    const newScrollTop = containerRef.value.scrollTop;
    scrollTop.value = newScrollTop;
    // 使用 requestAnimationFrame 进行节流
    if (!scrollAnimationFrame) {
      scrollAnimationFrame = requestAnimationFrame(() => {
        scrollAnimationFrame = null;
        calculateVisibleRange();
      });
    }
  };

  // 高度测量
  const measureItemHeight = (index, el) => {
    if (!el || !positions.value[index]) return;
    const item = renderDataSource.value[index];
    if (!item) return;
    queueMicrotask(() => {
      try {
        const pos = positions.value[index];
        // 分组行和数据行强制重新计算高度
        const actualHeight = item?.level ? getEstimatedHeight(item) : el.getBoundingClientRect()?.height;
        // 高度变化超过阈值才更新
        if (Math.abs(actualHeight - pos.height) > 2) {
          const diff = actualHeight - pos.height;
          pos.height = actualHeight;
          pos.bottom += diff;
          // 更新后续所有行位置
          for (let i = index + 1; i < positions.value.length; i++) {
            if (positions.value[i]) {
              positions.value[i].top += diff;
              positions.value[i].bottom += diff;
            }
          }
          // 保持滚动位置
          if (scrollTop.value > pos.top) {
            scrollTop.value += diff;
            containerRef.value?.scrollTo({ top: scrollTop.value, behavior: 'instant' });
          }
          // 重新计算可见区域
          calculateVisibleRange();
        }
      } catch (e) {
        console.error('Height measurement error:', e);
      }
    });
  };
  // 重新计算
  const recalculate = () => {
    if (isUpdating.value) return;
    isUpdating.value = true;
    requestAnimationFrame(() => {
      try {
        updatePositions();
        nextTick(() => {
          updateContainerHeight();
          calculateVisibleRange();
        });
      } finally {
        isUpdating.value = false;
      }
    });
  };

  // 挂载/卸载
  onMounted(() => {
    if (!ensureContainer()) return;
    updatePositions();
    updateContainerHeight();
    calculateVisibleRange();
    containerRef.value.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', recalculate);
  });

  onUnmounted(() => {
    if (containerRef.value) {
      containerRef.value.removeEventListener('scroll', handleScroll);
    }
    if (scrollAnimationFrame) {
      cancelAnimationFrame(scrollAnimationFrame);
    }
    window.removeEventListener('resize', recalculate);
  });

  // 防抖函数
  const debounceRecalculation = (fn, delay) => {
    let timeoutId;
    let lastArgs;
    let lastThis;
    let needsUpdate = false;
    return function (...args) {
      lastArgs = args;
      lastThis = this;
      needsUpdate = true;
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          if (needsUpdate) {
            fn.apply(lastThis, lastArgs);
          }
          timeoutId = null;
          needsUpdate = false;
        }, delay);
      }
    };
  };
  const optimizedRecalculate = debounceRecalculation(() => {
    if (isUpdating.value) return;
    queueMicrotask(() => {
      if (isUpdating.value) return;
      isUpdating.value = true;
      try {
        updatePositions();
        nextTick(() => {
          updateContainerHeight();
          calculateVisibleRange();
        });
      } finally {
        isUpdating.value = false;
      }
    });
  }, 20);
  // 监听优化
  watch(dataSource, optimizedRecalculate, { deep: true });
  watch(groupState, optimizedRecalculate, { deep: true });

  return {
    visibleData,
    paddingTop,
    paddingBottom,
    totalHeight,
    measureItemHeight,
    recalculate,
    updateGroupState,
    onGroupEnterViewport
  };
}
